import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { Wallet, Sparkles, AlertCircle, Clock, History, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { Modal } from '../../components/ui/Modal'
import { Button } from '../../components/ui/Button'

type Preset = 'today' | '7d' | '30d' | 'month' | 'custom'

interface SummaryResponse {
  from: string
  to: string
  totalCostUsd: string
  totalCalls: number
  errorCalls: number
  totalInputTokens: number
  totalOutputTokens: number
  avgCostPerCall: string
  avgDurationMs: number
  activeModels: Array<{ model: string; calls: number; costUsd: string }>
}

interface BreakdownRow {
  key: string
  label: string
  calls: number
  inputTokens: number
  outputTokens: number
  costUsd: string
  errorCalls: number
}

interface TimeseriesResponse {
  from: string
  to: string
  granularity: 'day' | 'hour'
  rows: Array<{ bucket: string; calls: number; costUsd: string }>
}

function startOfDay(d: Date): Date {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

function getRange(preset: Preset, customFrom: string, customTo: string): { from: Date; to: Date } {
  const now = new Date()
  switch (preset) {
    case 'today': {
      return { from: startOfDay(now), to: now }
    }
    case '7d': {
      return { from: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000), to: now }
    }
    case '30d': {
      return { from: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000), to: now }
    }
    case 'month': {
      return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: now }
    }
    case 'custom': {
      const from = customFrom
        ? new Date(customFrom)
        : new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
      const to = customTo ? new Date(customTo + 'T23:59:59') : now
      return { from, to }
    }
  }
}

function formatUsd(s: string | number): string {
  const n = typeof s === 'string' ? Number(s) : s
  return `$${n.toFixed(n < 1 ? 4 : 2)}`
}

function formatDate(iso: string, granularity: 'day' | 'hour'): string {
  const d = new Date(iso)
  if (granularity === 'hour') {
    return d.toLocaleString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }
  return d.toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit' })
}

interface BackfillResponse {
  insertedCount: number
  deletedCount: number
  totalCostUsd: string
  costPerCall: string
  calibrated: boolean
  realLoggedInCalibPeriod?: string
  untrackedCostUsd?: string
  actualTotalUsd?: string
  message: string
}

export function SetariAiUsageTab() {
  const queryClient = useQueryClient()
  const [preset, setPreset] = useState<Preset>('30d')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [backfillModalOpen, setBackfillModalOpen] = useState(false)

  const range = useMemo(
    () => getRange(preset, customFrom, customTo),
    [preset, customFrom, customTo],
  )
  const params = useMemo(
    () => ({ from: range.from.toISOString(), to: range.to.toISOString() }),
    [range],
  )

  const summaryQ = useQuery<SummaryResponse>({
    queryKey: ['ai-usage-summary', params],
    queryFn: async () =>
      (
        await api.get<SummaryResponse>('/settings/ai/usage/summary', {
          params,
        })
      ).data,
  })

  // Costul facturat. Fereastra e fixă la 30 de zile, independentă de filtrul de sus:
  // raportul Anthropic e pe zile calendaristice, iar o comparație pe „azi" sau „7 zile"
  // ar arăta diferențe care vin doar din decalajul de închidere a zilei, nu din consum.
  const billedQ = useQuery<{
    billedUsd: number
    trackedUsd: number
    untrackedUsd: number
    coveragePct: number | null
    adminKeySet: boolean
  }>({
    queryKey: ['ai-billed'],
    queryFn: async () =>
      (await api.get('/settings/ai/usage/billed', { params: { days: 30 } })).data,
  })
  const billed = billedQ.data

  const syncBilled = useMutation({
    mutationFn: async () => (await api.post('/settings/ai/usage/billed/sync')).data,
    onSuccess: () => {
      toast.success('Cost facturat actualizat')
      queryClient.invalidateQueries({ queryKey: ['ai-billed'] })
    },
    onError: () =>
      toast.error('Nu s-a putut citi costul facturat. Verifică cheia Admin în Setări → AI.'),
  })

  const timeseriesQ = useQuery<TimeseriesResponse>({
    queryKey: ['ai-usage-timeseries', params],
    queryFn: async () =>
      (
        await api.get<TimeseriesResponse>('/settings/ai/usage/timeseries', {
          params: { ...params, granularity: 'day' },
        })
      ).data,
  })

  const moduleQ = useQuery<{ rows: BreakdownRow[] }>({
    queryKey: ['ai-usage-breakdown', 'module', params],
    queryFn: async () =>
      (
        await api.get<{ rows: BreakdownRow[] }>('/settings/ai/usage/breakdown', {
          params: { ...params, by: 'module' },
        })
      ).data,
  })

  const modelQ = useQuery<{ rows: BreakdownRow[] }>({
    queryKey: ['ai-usage-breakdown', 'model', params],
    queryFn: async () =>
      (
        await api.get<{ rows: BreakdownRow[] }>('/settings/ai/usage/breakdown', {
          params: { ...params, by: 'model' },
        })
      ).data,
  })

  const userQ = useQuery<{ rows: BreakdownRow[] }>({
    queryKey: ['ai-usage-breakdown', 'user', params],
    queryFn: async () =>
      (
        await api.get<{ rows: BreakdownRow[] }>('/settings/ai/usage/breakdown', {
          params: { ...params, by: 'user' },
        })
      ).data,
  })

  const summary = summaryQ.data
  const peakDailyCost = useMemo(() => {
    const rows = timeseriesQ.data?.rows ?? []
    if (rows.length === 0) return 0
    return Math.max(...rows.map((r) => Number(r.costUsd)))
  }, [timeseriesQ.data])

  const chartData = useMemo(() => {
    return (timeseriesQ.data?.rows ?? []).map((r) => ({
      bucket: formatDate(r.bucket, 'day'),
      cost: Number(r.costUsd),
      calls: r.calls,
    }))
  }, [timeseriesQ.data])

  return (
    <div className="space-y-4">
      {/* Filtru perioada */}
      <div className="flex flex-wrap items-center gap-2 rounded border border-b4l-border bg-white p-3">
        <span className="text-xs font-medium text-b4l-text-muted">Perioadă:</span>
        {(['today', '7d', '30d', 'month'] as Preset[]).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPreset(p)}
            className={`rounded px-3 py-1 text-xs ${
              preset === p
                ? 'bg-b4l-green text-white'
                : 'border border-b4l-border bg-white text-b4l-text hover:bg-b4l-bg-soft'
            }`}
          >
            {p === 'today'
              ? 'Astăzi'
              : p === '7d'
                ? '7 zile'
                : p === '30d'
                  ? '30 zile'
                  : 'Luna curentă'}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setPreset('custom')}
          className={`rounded px-3 py-1 text-xs ${
            preset === 'custom'
              ? 'bg-b4l-green text-white'
              : 'border border-b4l-border bg-white text-b4l-text hover:bg-b4l-bg-soft'
          }`}
        >
          Custom
        </button>
        {preset === 'custom' && (
          <>
            <input
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="rounded border border-b4l-border px-2 py-1 text-xs"
            />
            <span className="text-xs text-b4l-text-muted">→</span>
            <input
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              className="rounded border border-b4l-border px-2 py-1 text-xs"
            />
          </>
        )}
        <span className="ml-auto text-xs text-b4l-text-dim">
          {range.from.toLocaleDateString('ro-RO')} – {range.to.toLocaleDateString('ro-RO')}
        </span>
        <button
          type="button"
          onClick={() => setBackfillModalOpen(true)}
          className="inline-flex items-center gap-1 rounded border border-b4l-border bg-white px-2 py-1 text-xs text-b4l-text-muted hover:bg-b4l-bg-soft hover:text-b4l-text"
          title="Reconstituie consum istoric pentru taskExtractor (estimare sau calibrat cu cifra Anthropic Console)"
        >
          <History className="h-3 w-3" />
          Backfill istoric
        </button>
      </div>

      {backfillModalOpen && (
        <BackfillModal
          onClose={() => setBackfillModalOpen(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['ai-usage-summary'] })
            queryClient.invalidateQueries({ queryKey: ['ai-usage-timeseries'] })
            queryClient.invalidateQueries({ queryKey: ['ai-usage-breakdown'] })
          }}
        />
      )}

      {/* Factura reală de la Anthropic vs. ce vede CRM-ul. Diferența nu e o eroare de
          contorizare: aceeași cheie e folosită și de boost4life-intel și de scripturile
          din AGENTI, care nu scriu în `ai_usage_log`. */}
      {billed && billed.adminKeySet && billed.billedUsd > 0 && (
        <div className="rounded-lg border border-b4l-blue/30 bg-b4l-blue/5 p-3 text-sm">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span>
              Facturat de Anthropic, ultimele 30 de zile: <b>${billed.billedUsd.toFixed(2)}</b> ·
              contorizat aici <b>${billed.trackedUsd.toFixed(2)}</b>
              {billed.coveragePct != null && <> ({billed.coveragePct}%)</>}
            </span>
            <button
              className="text-xs font-medium text-b4l-blue underline"
              onClick={() => syncBilled.mutate()}
              disabled={syncBilled.isPending}
            >
              {syncBilled.isPending ? 'Se sincronizează…' : 'Actualizează'}
            </button>
          </div>
          {billed.untrackedUsd > 0.5 && (
            <div className="mt-1 text-xs text-b4l-text-muted">
              Diferența de ${billed.untrackedUsd.toFixed(2)} vine din aplicațiile care folosesc
              aceeași cheie fără să raporteze aici (boost4life-intel, AGENTI).
            </div>
          )}
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <KpiCard
          icon={Wallet}
          label="Total cost"
          value={summary ? formatUsd(summary.totalCostUsd) : '—'}
          accent="text-b4l-green"
          subtitle={
            billed && billed.adminKeySet && billed.billedUsd > 0
              ? `din $${billed.billedUsd.toFixed(2)} facturați`
              : 'doar apelurile CRM'
          }
        />
        <KpiCard
          icon={Sparkles}
          label="Total apeluri"
          value={summary ? String(summary.totalCalls) : '—'}
          accent="text-b4l-blue"
          subtitle={
            summary && summary.errorCalls > 0
              ? `${summary.errorCalls} erori`
              : summary
                ? 'fără erori'
                : ''
          }
        />
        <KpiCard
          icon={Clock}
          label="Cost mediu/apel"
          value={summary ? formatUsd(summary.avgCostPerCall) : '—'}
          accent="text-b4l-purple"
          subtitle={summary ? `~${summary.avgDurationMs}ms/apel` : ''}
        />
        <KpiCard
          icon={AlertCircle}
          label="Vârf zilnic"
          value={timeseriesQ.data ? formatUsd(peakDailyCost) : '—'}
          accent="text-b4l-amber"
        />
      </div>

      {/* Empty state */}
      {summary && summary.totalCalls === 0 && (
        <div className="rounded border border-b4l-border bg-b4l-bg-soft p-6 text-center text-sm text-b4l-text-muted">
          Nu există apeluri AI înregistrate în perioada selectată. Tabel-ul{' '}
          <code className="rounded bg-white px-1 font-mono text-xs">ai_usage_log</code> se populează
          automat la fiecare apel Claude (clasificare email, brief AI, marketing copy etc).
        </div>
      )}

      {/* Chart */}
      {summary && summary.totalCalls > 0 && (
        <div className="rounded border border-b4l-border bg-white p-4">
          <div className="mb-2 text-sm font-semibold text-b4l-text">Cost zilnic (USD)</div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="bucket" style={{ fontSize: 11 }} />
              <YAxis style={{ fontSize: 11 }} tickFormatter={(v) => `$${v.toFixed(2)}`} />
              <Tooltip
                formatter={(v, name) => {
                  const num = typeof v === 'number' ? v : Number(v)
                  if (name === 'cost') return [formatUsd(num), 'Cost']
                  return [String(v), 'Apeluri']
                }}
                contentStyle={{ fontSize: 12, padding: '6px 10px' }}
              />
              <Line type="monotone" dataKey="cost" stroke="#1D9E75" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* 3 tabele breakdown */}
      {summary && summary.totalCalls > 0 && (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
          <BreakdownTable title="Pe modul" rows={moduleQ.data?.rows ?? []} />
          <BreakdownTable title="Pe model" rows={modelQ.data?.rows ?? []} />
          <BreakdownTable title="Pe utilizator" rows={userQ.data?.rows ?? []} />
        </div>
      )}
    </div>
  )
}

function KpiCard({
  icon: Icon,
  label,
  value,
  accent,
  subtitle,
}: {
  icon: typeof Wallet
  label: string
  value: string
  accent: string
  subtitle?: string
}) {
  return (
    <div className="rounded border border-b4l-border bg-white p-3 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`rounded bg-b4l-bg-soft p-2 ${accent}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="text-[11px] uppercase text-b4l-text-muted">{label}</div>
          <div className="text-xl font-semibold text-b4l-text">{value}</div>
          {subtitle && <div className="text-[10px] text-b4l-text-dim">{subtitle}</div>}
        </div>
      </div>
    </div>
  )
}

function BreakdownTable({ title, rows }: { title: string; rows: BreakdownRow[] }) {
  return (
    <div className="rounded border border-b4l-border bg-white">
      <div className="border-b border-b4l-border px-3 py-2 text-xs font-semibold text-b4l-text">
        {title}
      </div>
      {rows.length === 0 ? (
        <div className="p-3 text-xs italic text-b4l-text-muted">— niciun rezultat —</div>
      ) : (
        <div className="max-h-72 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-b4l-bg-soft text-b4l-text-muted">
              <tr>
                <th className="px-3 py-1.5 text-left font-medium">Cheie</th>
                <th className="px-3 py-1.5 text-right font-medium">Apeluri</th>
                <th className="px-3 py-1.5 text-right font-medium">Cost USD</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-t border-b4l-border">
                  <td className="px-3 py-1.5 font-mono text-[11px] text-b4l-text" title={r.label}>
                    {r.label}
                  </td>
                  <td className="px-3 py-1.5 text-right text-b4l-text">{r.calls}</td>
                  <td className="px-3 py-1.5 text-right font-semibold text-b4l-green-dark">
                    {formatUsd(r.costUsd)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function BackfillModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [daysBack, setDaysBack] = useState(90)
  const [calibrate, setCalibrate] = useState(true)
  const [actualTotalUsd, setActualTotalUsd] = useState('')
  const [actualPeriodFrom, setActualPeriodFrom] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10),
  )
  const [result, setResult] = useState<BackfillResponse | null>(null)

  const mutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { daysBack }
      if (calibrate && actualTotalUsd) {
        body.actualTotalUsd = Number(actualTotalUsd)
        if (actualPeriodFrom) {
          body.actualPeriodFrom = new Date(actualPeriodFrom + 'T00:00:00').toISOString()
        }
      }
      return (await api.post<BackfillResponse>('/settings/ai/usage/backfill-task-extractor', body))
        .data
    },
    onSuccess: (data) => {
      setResult(data)
      toast.success('Reconstituire completă')
      onSuccess()
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : 'Reconstituirea a eșuat')
    },
  })

  return (
    <Modal
      open
      onClose={onClose}
      title="Reconstituire istoric consum"
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {result ? 'Închide' : 'Anulează'}
          </Button>
          {!result && (
            <Button
              onClick={() => mutation.mutate()}
              disabled={
                mutation.isPending ||
                (calibrate && (!actualTotalUsd || Number(actualTotalUsd) <= 0))
              }
            >
              {mutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <History className="h-3.5 w-3.5" />
              )}
              Rulează reconstituirea
            </Button>
          )}
        </div>
      }
    >
      {result ? (
        <div className="space-y-3 text-sm">
          <div className="rounded border border-b4l-green bg-b4l-green-light p-3 text-b4l-green-dark">
            <div className="font-semibold">✓ Reconstituire completă</div>
            <div className="mt-1 text-xs">{result.message}</div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <Stat label="Rânduri adăugate" value={String(result.insertedCount)} />
            <Stat label="Rânduri vechi șterse" value={String(result.deletedCount)} />
            <Stat label="Cost pe apel" value={`$${result.costPerCall}`} />
            <Stat label="Total reconstituit" value={`$${Number(result.totalCostUsd).toFixed(2)}`} />
            {result.calibrated && (
              <>
                <Stat
                  label="Real înregistrat (post-M1)"
                  value={`$${Number(result.realLoggedInCalibPeriod ?? 0).toFixed(2)}`}
                />
                <Stat label="Anthropic Console total" value={`$${result.actualTotalUsd ?? '?'}`} />
              </>
            )}
          </div>
          {result.calibrated && (
            <div className="rounded bg-b4l-bg-soft p-2 text-[11px] text-b4l-text-muted">
              <strong>Verificare:</strong> total reconstituit ($
              {Number(result.totalCostUsd).toFixed(2)}) + real înregistrat ($
              {Number(result.realLoggedInCalibPeriod ?? 0).toFixed(2)}) = $
              {(Number(result.totalCostUsd) + Number(result.realLoggedInCalibPeriod ?? 0)).toFixed(
                2,
              )}{' '}
              ≈ Anthropic Console (${result.actualTotalUsd}). Diferența mică (sub 0,01 $) vine din
              rotunjire.
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-xs text-b4l-text-muted">
            Reconstituie consumul estimat pentru clasificarea automată a emailurilor: pentru fiecare
            task creat automat dintr-un email se adaugă o înregistrare cu costul estimat. Poți relua
            oricând — reconstituirile vechi din aceeași perioadă se înlocuiesc, nu se dublează.
          </p>

          <div>
            <label className="mb-1 block text-xs font-medium text-b4l-text-muted">
              Câte zile înapoi?
            </label>
            <input
              type="number"
              value={daysBack}
              onChange={(e) => setDaysBack(Number(e.target.value))}
              min={1}
              max={365}
              className="w-32 rounded border border-b4l-border px-3 py-2 text-sm outline-none focus:border-b4l-green"
            />
            <span className="ml-2 text-xs text-b4l-text-muted">(1-365, implicit 90)</span>
          </div>

          <div className="rounded border border-b4l-border bg-b4l-bg-soft p-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={calibrate}
                onChange={(e) => setCalibrate(e.target.checked)}
              />
              <span className="font-semibold">Calibrare cu cifra reală din Anthropic Console</span>
            </label>
            <p className="mt-1 text-[11px] text-b4l-text-muted">
              Recomandat. Ia cifra de pe console.anthropic.com → Cost → API key CRM boost4life
              pentru luna / perioada în care vrei să potrivești totalul exact.
            </p>

            {calibrate && (
              <div className="mt-3 space-y-2">
                <div>
                  <label className="mb-1 block text-xs font-medium text-b4l-text-muted">
                    Total USD din Anthropic Console
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">$</span>
                    <input
                      type="number"
                      step="0.01"
                      value={actualTotalUsd}
                      onChange={(e) => setActualTotalUsd(e.target.value)}
                      placeholder="ex: 12.66"
                      className="w-32 rounded border border-b4l-border px-3 py-2 text-sm outline-none focus:border-b4l-green"
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-b4l-text-muted">
                    Pentru perioada de la
                  </label>
                  <input
                    type="date"
                    value={actualPeriodFrom}
                    onChange={(e) => setActualPeriodFrom(e.target.value)}
                    className="rounded border border-b4l-border px-3 py-2 text-sm outline-none focus:border-b4l-green"
                  />
                  <span className="ml-2 text-xs text-b4l-text-muted">
                    (implicit: prima zi a lunii curente)
                  </span>
                </div>
                <p className="text-[11px] italic text-b4l-text-muted">
                  Logica: distribuie ($actualTotal − totalul deja înregistrat real post-M1) între
                  task-urile email_auto din perioadă. Rezultatul: total CRM ≈ total Anthropic
                  Console exact.
                </p>
              </div>
            )}
          </div>

          {!calibrate && (
            <div className="rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
              Fără calibrare, costul pe apel e estimat la aprox. 0,0055 $ (3000 tokeni intrare + 500
              ieșire × prețul curent). Precizie: ±20 %.
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded border border-b4l-border bg-white p-2">
      <div className="text-[10px] uppercase text-b4l-text-muted">{label}</div>
      <div className="font-semibold text-b4l-text">{value}</div>
    </div>
  )
}
